import type { LucideIcon } from "lucide-react";
import {
  ActivityIcon,
  AlertTriangleIcon,
  CodeXmlIcon,
  GitBranchIcon,
  LayersIcon,
  ShieldIcon,
  SparklesIcon,
  TableIcon,
  WorkflowIcon,
} from "lucide-react";

export type LandingPain = {
  readonly description: string;
  readonly icon: LucideIcon;
  readonly title: string;
};

export type LandingPillar = {
  readonly description: string;
  readonly icon: LucideIcon;
  readonly outcome: string;
  readonly title: string;
};

export type LandingComparisonRow = {
  readonly capability: string;
  readonly linguLink: string;
  readonly spreadsheets: string;
  readonly tms: string;
};

export type LandingStep = {
  readonly body: string;
  readonly title: string;
};

export type LandingFeatureGroup = {
  readonly items: readonly string[];
  readonly title: string;
};

export type LandingFaq = {
  readonly answer: string;
  readonly question: string;
};

export type LandingTrustLink = {
  readonly description: string;
  readonly href: string;
  readonly icon: LucideIcon;
  readonly label: string;
};

export type LandingEditorRow = {
  readonly key: string;
  readonly status: "approved" | "draft" | "missing";
  readonly value: string;
};

/** Hero copy: leads with the ICP and a concrete promise instead of a generic tagline. */
export const landingHero = {
  badge: "Built for shipping teams",
  bullets: [
    "Replace spreadsheets and ad-hoc PR diffs with a single source of truth",
    "Per-environment overrides — dev, staging, and production never drift",
    "AI-assisted translation across OpenAI, Claude, and Llama with automatic fallback",
    "REST API + Slack hooks so localization fits your existing release flow",
  ],
  description:
    "LinguLink is a localization workspace for product engineering teams that ship multi-language SaaS. Manage projects, keys, and locales with diffs you can audit, exports your runtime already understands, and integrations that meet your stack where it is.",
  icpLine:
    "If your team writes copy in Notion, ships strings in PR diffs, or syncs translations through spreadsheets — this is the workflow you've been hand-rolling.",
  primaryCtaLabel: "Create your workspace",
  secondaryCtaLabel: "See how it works",
  title: "Localization that actually fits your release pipeline",
} as const;

/** Editor preview rows for the hero mock — chosen to demonstrate real product surface, not abstract metrics. */
export const landingEditorRows: readonly LandingEditorRow[] = [
  { key: "checkout.button.confirm", status: "approved", value: "Confirm and pay" },
  { key: "checkout.error.declined", status: "draft", value: "Your card was declined." },
  { key: "checkout.shipping.eta", status: "missing", value: "—" },
  { key: "onboarding.step1.title", status: "approved", value: "Create your workspace" },
  { key: "billing.plan.cta", status: "draft", value: "Upgrade to Team" },
];

export const landingEditorLocales = ["en-US", "de-DE", "fr-FR", "tr-TR", "es-ES"] as const;

/** The "why" section: names real pains so readers see themselves, then nods at the cure. */
export const landingPains: readonly LandingPain[] = [
  {
    description:
      "Translations live in five Google Sheets, three Notion docs, and a Slack thread. Nobody knows which one shipped to production.",
    icon: TableIcon,
    title: "Spreadsheets stop scaling",
  },
  {
    description:
      "Engineers paste new keys into PRs. Reviewers approve them without context. Two weeks later a missing locale crashes the German checkout.",
    icon: AlertTriangleIcon,
    title: "PR diffs hide regressions",
  },
  {
    description:
      "Staging shows the new copy, production still shows last quarter's. Per-environment overrides become a manual export-and-pray ritual.",
    icon: GitBranchIcon,
    title: "Environments drift silently",
  },
];

/** Outcome-driven pillars that turn translation into a real release artifact. */
export const landingPillars: readonly LandingPillar[] = [
  {
    description:
      "Projects, keys, locales, and history live behind a single editor. Diffs make every change reviewable; namespaces keep large catalogs navigable.",
    icon: LayersIcon,
    outcome: "One workspace, one source of truth",
    title: "Structured translation editor",
  },
  {
    description:
      "Per-environment overrides for dev, staging, and production. Exports as JSON, CSV, YAML, XML, or XLIFF — formats your runtime already speaks.",
    icon: WorkflowIcon,
    outcome: "Ships into your existing pipeline",
    title: "Environments + exports built in",
  },
  {
    description:
      "Organization-scoped REST API, API keys for automation, and Slack notifications when copy changes. Managed AI-assisted fills across OpenAI, Claude, and Llama with automatic fallback.",
    icon: CodeXmlIcon,
    outcome: "Plays nicely with your stack",
    title: "Developer-grade integrations",
  },
  {
    description:
      "Clerk-backed organizations with roles and permissions. Audit-friendly visibility into who changed what — and when — across every locale.",
    icon: ShieldIcon,
    outcome: "Teams without chaos",
    title: "Governance that doesn't get in the way",
  },
];

/** Comparison row — concrete differentiators against the two real alternatives most teams consider. */
export const landingComparison: readonly LandingComparisonRow[] = [
  {
    capability: "Per-environment overrides",
    linguLink: "Built in for dev, staging, prod",
    spreadsheets: "Manual sheet duplication",
    tms: "Add-on or premium tier",
  },
  {
    capability: "Audit-grade change history",
    linguLink: "Diff per key, per locale, per author",
    spreadsheets: "Cell history, if you remember to look",
    tms: "Available, often paywalled",
  },
  {
    capability: "AI translation provider",
    linguLink: "Managed OpenAI / Claude / Llama with fallback",
    spreadsheets: "Copy-paste into ChatGPT",
    tms: "Vendor-locked engine, per-word billing",
  },
  {
    capability: "Engineering automation",
    linguLink: "REST API + org-scoped API keys + Slack",
    spreadsheets: "None",
    tms: "Webhooks + CLI, often complex",
  },
  {
    capability: "Onboarding new teammates",
    linguLink: "Clerk org invite, roles ready",
    spreadsheets: "Share permissions, hope for the best",
    tms: "Seat licensing, admin console",
  },
];

export const landingSteps: readonly LandingStep[] = [
  {
    body: "Sign up with Clerk and spin up an organization. Invite engineers, designers, and translators with roles.",
    title: "Create your workspace",
  },
  {
    body: "Add a project, define your locales, and import existing strings from JSON, CSV, YAML, XML, or XLIFF.",
    title: "Bring your strings in",
  },
  {
    body: "Wire up storage (S3 / GCS), Slack notifications, and your AI provider. Pull translations through the REST API or scheduled exports.",
    title: "Connect your pipeline",
  },
  {
    body: "Edit keys with diffs, override per environment, and ship to production with the same review rigor as code.",
    title: "Ship with confidence",
  },
];

/** Integrations grouped by purpose. Each item must reflect what the product actually ships today. */
export const landingIntegrations: readonly LandingFeatureGroup[] = [
  {
    items: ["Slack notifications when keys change, with org-level routing"],
    title: "Team visibility",
  },
  {
    items: [
      "AWS S3 and Google Cloud Storage (OAuth) at the organization level",
      "Per-environment storage routing per project (dev / staging / prod)",
    ],
    title: "Object storage",
  },
  {
    items: [
      "Managed OpenAI, Claude, and Llama with automatic provider fallback",
      "AI-assisted bulk translation metered by your plan's character allowance",
    ],
    title: "AI providers",
  },
  {
    items: [
      "REST API documented via OpenAPI",
      "Organization-scoped API keys for automation and internal tools",
      "Official lingulink CLI for pull/push/status in scripts and CI",
    ],
    title: "Automation & extensibility",
  },
];

export const landingFeatureCatalog: readonly LandingFeatureGroup[] = [
  {
    items: [
      "Project, language, and locale management",
      "Key/value editor with search, filters, and namespaces",
      "Translation history with diffs and change attribution",
      "Notifications with read/unread workflows",
    ],
    title: "Core platform",
  },
  {
    items: [
      "Bulk operations to update many keys at once",
      "AI-powered translation with multiple providers",
      "Status updates and review workflows",
      "Coverage view to spot missing keys per locale",
    ],
    title: "Shipping workflows",
  },
  {
    items: [
      "Exports: JSON, CSV, YAML, XML, XLIFF",
      "Per-environment overrides for dev, staging, and production",
      "REST API + organization-scoped API keys",
      "CLI for pulling, pushing, and gating CI on coverage",
      "Slack notifications for translation changes",
    ],
    title: "Developer + API",
  },
  {
    items: [
      "Clerk-backed organizations with roles and permissions",
      "Usage-aware plans and permission-based feature gating",
      "Audit-friendly visibility into who changed what — and when",
      "SLA-backed support available on Enterprise",
    ],
    title: "Teams + governance",
  },
];

export const landingFaqs: readonly LandingFaq[] = [
  {
    answer:
      "Yes. Start with a single project and one locale, then scale to multi-team orgs with role-based access. The editor and review flow stay the same shape as you grow.",
    question: "Does LinguLink work for both small teams and larger orgs?",
  },
  {
    answer:
      "Treat translation keys the way you treat code: create, review, and ship updates with diffs and history. Pull strings via the CLI, REST API, or scheduled exports — your runtime never has to change shape. The CLI's status --min flag gates CI on coverage.",
    question: "How does it fit into our engineering workflow?",
  },
  {
    answer:
      "No. The editor encourages a clean structure but doesn't lock you in. Namespaces and bulk operations let teams refactor key conventions later without losing history.",
    question: "Do we need a perfect key structure on day one?",
  },
  {
    answer:
      "LinguLink uses Clerk for authentication, so it integrates naturally with Next.js + Clerk apps. The REST API works with any framework — Next.js, Remix, SvelteKit, Rails, FastAPI, you name it.",
    question: "Does it work with Next.js, Clerk, and other stacks?",
  },
  {
    answer:
      "AI translation runs through managed providers — OpenAI, Claude, and Llama with automatic fallback — so there are no per-word vendor fees or keys to wire up. Bulk AI fills draw from your plan's monthly character allowance; once it's spent, AI translation pauses until the next billing period.",
    question: "How does AI-assisted translation get billed?",
  },
  {
    answer:
      "Self-serve checkout is being finalized. Plans on the pricing section are shown for reference. Enterprise teams can talk to us today about SLA-backed support and procurement.",
    question: "Is billing and checkout available today?",
  },
];

export const landingTrustLinks: readonly LandingTrustLink[] = [
  {
    description: "Maintenance windows, recent incidents, and how to reach us about availability.",
    href: "/status",
    icon: ActivityIcon,
    label: "System status",
  },
  {
    description: "Encryption, access controls, and how we handle organization data.",
    href: "/security",
    icon: ShieldIcon,
    label: "Security overview",
  },
];

/** Five concrete archetypes used as ICP chips under the hero copy. */
export const landingIcpArchetypes = [
  "Multi-region SaaS teams",
  "B2C apps with 5+ locales",
  "Open-source maintainers",
  "Localization agencies",
  "Mobile + web product orgs",
] as const;

export const landingHeroBadgeIcon: LucideIcon = SparklesIcon;
